import {ToJSON_Advanced} from "js-vextensions";
import {toJS} from "mobx";

// todo: probably push this to js-vextensions again (as new version of CloneWithPrototypes) -- albeit, maybe with some slight adjustments

/** Variant of Clone which preserves prototypes, non-enumerable properties (if enabled), and circular links (if enabled). */
export function CloneSafe<T>(originalObject: T, transferNonEnumerableProps = false, keepCircularLinks = false): T {
	if (originalObject == null) return originalObject;

	const copies = [{
		source: originalObject,
		target: Array.isArray(originalObject) ? [] : Object.create(Object.getPrototypeOf(originalObject)),
	}];
	const cloneObject = copies[0].target;
	const sourceReferences = [originalObject];
	const targetReferences = [cloneObject];

	// first in, first out
	let current;
	while (current = copies.shift()) {
		const keys = transferNonEnumerableProps ? Object.getOwnPropertyNames(current.source) : Object.keys(current.source);

		for (const key of keys) {
			// Save the source's descriptor
			const descriptor = Object.getOwnPropertyDescriptor(current.source, key)!;

			if (!descriptor.value || typeof descriptor.value !== "object") {
				Object.defineProperty(current.target, key, descriptor);
				continue;
			}

			const nextSource = descriptor.value;
			descriptor.value = Array.isArray(nextSource) ? [] : Object.create(Object.getPrototypeOf(nextSource));

			if (keepCircularLinks) {
				const indexOf = sourceReferences.indexOf(nextSource);

				if (indexOf !== -1) {
					// The source is already referenced, just assign reference
					descriptor.value = targetReferences[indexOf];
					Object.defineProperty(current.target, key, descriptor);
					continue;
				}

				sourceReferences.push(nextSource);
				targetReferences.push(descriptor.value);
			}

			Object.defineProperty(current.target, key, descriptor);

			copies.push({source: nextSource, target: descriptor.value});
		}
	}

	return cloneObject;
}

/** Variant of Clone that works for instances of classes with `@O accessor myProp = ...` props. (mobx 7+ pattern)
 * (it keeps the class-prototype + copies the value stored for each such mobx-accessor) */
export function CloneMobXClassInstance<T extends object>(instance: T, constructorArgs?: any[]): T {
  if (instance === null || typeof instance !== 'object') return instance;
  const rawValues = toJS(instance); // strip MobX reactivity and get raw deep values
  const deepClonedValues = structuredClone(rawValues); // deep clone the raw values (ensuring no data-sharing at deeper levels)
  const Constructor = instance.constructor as new(...args: any[]) => T; // get the original class constructor
  const clone = new Constructor(...(constructorArgs ?? [])); // create a completely new instance
  Object.assign(clone, deepClonedValues); // apply the cloned values
  return clone;
}

// ---------------------------------------------------------------------------
// Collapsing pretty-printer
// ---------------------------------------------------------------------------

/** Number of scalar elements a primitive-only array may have and still be collapsed onto a single line. */
const MAX_COLLAPSIBLE_ARRAY_ITEMS = 4;
/** Number of scalar-valued properties an object may have and still be collapsed onto a single line. */
const MAX_COLLAPSIBLE_OBJECT_PROPS = 3;

/** True if `v` serializes as a single scalar (number/string/boolean/null/undefined) — i.e. it needs no braces/brackets. */
function IsSimpleScalar(v: unknown): v is string | number | boolean | null | undefined {
	return v === null || v === undefined || ["number", "string", "boolean"].includes(typeof v);
}

/** Mirrors JSON.stringify's `toJSON` contract: an object's `toJSON` method, if present, wins. */
function ResolveJSONValue(v: unknown): unknown {
	return v && typeof v === "object" && typeof (v as any).toJSON === "function" ? (v as any).toJSON() : v;
}

/** Serializes a scalar value inline (no surrounding container). `undefined` becomes `null` (as JSON.stringify does for array elements). */
function SerializeScalarInline(v: unknown): string {
	if (v === undefined) return "null";
	return JSON.stringify(v) as string;
}

/** Whether an array collapses to a single line: few elements, all scalars. */
function IsCollapsibleArray(arr: unknown[]): boolean {
	return arr.length <= MAX_COLLAPSIBLE_ARRAY_ITEMS && arr.every(item => IsSimpleScalar(ResolveJSONValue(item)));
}

/** Whether an object collapses to a single line: few properties, all scalar values. */
function IsCollapsibleObject(obj: Record<string, unknown>): boolean {
	const keys = Object.keys(obj);
	return keys.length <= MAX_COLLAPSIBLE_OBJECT_PROPS && keys.every(key => IsSimpleScalar(ResolveJSONValue(obj[key])));
}

/** Pretty-prints JSON but collapses "simple" containers (few elements + all scalars) onto one line.
 *  Decision is by count/nature of props+values, NOT char length — so structurally-identical peers
 *  format identically. Non-simple containers expand with `indentation` (mirrors JSON.stringify's toJSON). */
export function ToJSON_Compact(value: unknown, indentation = "\t"): string {
	const serialize = (v: unknown, depth: number): string => {
		v = ResolveJSONValue(v);

		if (v === null) return "null";
		if (Array.isArray(v)) {
			if (v.length === 0) return "[]";
			if (IsCollapsibleArray(v)) {
				return `[${v.map(item => SerializeScalarInline(ResolveJSONValue(item))).join(", ")}]`;
			}
			const childIndent = indentation.repeat(depth + 1);
			const closeIndent = indentation.repeat(depth);
			return `[\n${v.map(item => childIndent + serialize(item, depth + 1)).join(",\n")}\n${closeIndent}]`;
		}
		if (typeof v === "object") {
			const keys = Object.keys(v as Record<string, unknown>);
			if (keys.length === 0) return "{}";
			if (IsCollapsibleObject(v as Record<string, unknown>)) {
				return `{${keys.map(key => `${JSON.stringify(key)}: ${SerializeScalarInline(ResolveJSONValue((v as any)[key]))}`).join(", ")}}`;
			}
			const childIndent = indentation.repeat(depth + 1);
			const closeIndent = indentation.repeat(depth);
			return `{\n${keys.map(key => `${childIndent}${JSON.stringify(key)}: ${serialize((v as any)[key], depth + 1)}`).join(",\n")}\n${closeIndent}}`;
		}
		return JSON.stringify(v) as string; // number, string, boolean
	};

	return serialize(value, 0);
}